import React from "react";
import ReactModal from "react-modal";
import { DirectUpload } from "@rails/activestorage";
import { post } from '@rails/request.js';
import PropTypes from "prop-types";

import { ALL_QUESTIONS, questionsFor, topicFor, topicLabel } from "../questions";
import QuestionBank from "./QuestionBank";
import VideoRecorder from "./VideoRecorder";
import { isSafari } from "../utils";

class ProfileVideoRecorder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      question: null,
      topicId: "all",
      answeredQuestions: props.answeredQuestions || [],
      error: '',
      success: null,
      modalOpen: false,
      bankOpen: false,
      isRecording: false,
      hasTake: false,
      uploadProgress: 0,
      uploadingQuestion: null,
      uploadsInFlight: 0,
      rerecording: false,
    }
  }

  questionsInTopic = (topicId = this.state.topicId) => {
    return questionsFor(topicId)
  }

  unansweredQuestions = (answered = this.state.answeredQuestions, topicId = this.state.topicId) => {
    return this.questionsInTopic(topicId).filter((question) => !answered.includes(question))
  }

  nextUnanswered = (answered = this.state.answeredQuestions, current = this.state.question, topicId = this.state.topicId) => {
    const pool = this.unansweredQuestions(answered, topicId).filter((question) => question !== current)
    if (pool.length) {
      const list = this.questionsInTopic(topicId)
      const idx = Math.max(0, list.indexOf(current))
      const rotated = list.slice(idx + 1).concat(list.slice(0, idx + 1))
      return rotated.find((question) => pool.includes(question)) || pool[0]
    }

    const remaining = ALL_QUESTIONS.filter((question) => !answered.includes(question) && question !== current)
    return remaining[0] || null
  }

  pickQuestion = (answered = this.state.answeredQuestions, current = this.state.question, topicId = this.state.topicId) => {
    const unanswered = this.unansweredQuestions(answered, topicId).filter((question) => question !== current)
    if (unanswered.length) {
      return unanswered[Math.floor(Math.random() * unanswered.length)]
    }
    return this.nextUnanswered(answered, current, "all")
  }

  confirmDiscardTake = () => {
    if (!this.state.isRecording && !this.state.hasTake) return true
    return window.confirm('Switch questions? This take will be discarded.')
  }

  setQuestion = (nextQuestion, { rerecording = false, topicId = this.state.topicId, closeBank = true } = {}) => {
    if (nextQuestion && nextQuestion !== this.state.question && !this.confirmDiscardTake()) return false

    this.setState({
      question: nextQuestion,
      topicId,
      rerecording,
      bankOpen: closeBank ? false : this.state.bankOpen,
      error: '',
    })
    return true
  }

  openModal = () => {
    const remaining = this.unansweredQuestions()
    const question = this.state.question || this.pickQuestion()
    this.setState({
      modalOpen: true,
      error: '',
      success: null,
      question,
      rerecording: false,
      bankOpen: remaining.length === 0,
    })
  }

  openForQuestion = (question) => {
    this.setState({
      modalOpen: true,
      question,
      topicId: topicFor(question),
      rerecording: true,
      bankOpen: false,
      error: '',
      success: null
    })
  }

  requestClose = () => {
    if (this.state.isRecording) {
      const confirmed = window.confirm('Stop recording and close? This take will be discarded.')
      if (!confirmed) return
    }

    this.setState({
      modalOpen: false,
      isRecording: false,
      hasTake: false,
      rerecording: false,
      bankOpen: false,
    })
  }

  handleTopic = (topicId) => {
    if (topicId === this.state.topicId) return

    const unanswered = this.unansweredQuestions(this.state.answeredQuestions, topicId)
    const keepCurrent = unanswered.includes(this.state.question)
    const nextQuestion = keepCurrent ? this.state.question : (unanswered[0] || this.state.question)
    const rerecording = nextQuestion ? this.state.answeredQuestions.includes(nextQuestion) : false

    if (nextQuestion !== this.state.question && !this.confirmDiscardTake()) return

    this.setState({
      topicId,
      question: nextQuestion,
      rerecording,
      error: '',
    })
  }

  handleSelectQuestion = (question) => {
    this.setQuestion(question, {
      rerecording: this.state.answeredQuestions.includes(question),
      topicId: this.state.topicId === "all" ? this.state.topicId : topicFor(question),
      closeBank: true,
    })
  }

  handleNext = () => {
    const nextQuestion = this.nextUnanswered()
    if (!nextQuestion) return
    this.setQuestion(nextQuestion, { rerecording: false })
  }

  handleShuffle = () => {
    const nextQuestion = this.pickQuestion()
    if (!nextQuestion) return
    this.setQuestion(nextQuestion, { rerecording: false })
  }

  handleAcceptedVideo = (blob, recordedQuestion) => {
    const { directUploadUrl, submitVideoPath } = this.props
    const question = recordedQuestion || this.state.question

    const mimeType = blob.type || (isSafari() ? 'video/mp4' : 'video/webm')
    const filename = mimeType.includes('mp4') ? 'video.mp4' : 'video.webm'
    const file = new File([blob], filename, { type: mimeType })

    const answeredQuestions = this.state.answeredQuestions.includes(question)
      ? this.state.answeredQuestions
      : this.state.answeredQuestions.concat(question)
    const nextQuestion = this.nextUnanswered(answeredQuestions, question)
    const nextTopic = nextQuestion && this.questionsInTopic().includes(nextQuestion)
      ? this.state.topicId
      : (nextQuestion ? topicFor(nextQuestion) : this.state.topicId)

    this.setState((prev) => ({
      uploadingQuestion: question,
      uploadsInFlight: prev.uploadsInFlight + 1,
      uploadProgress: 0,
      isRecording: false,
      hasTake: false,
      rerecording: false,
      bankOpen: !nextQuestion,
      answeredQuestions,
      question: nextQuestion,
      topicId: nextTopic,
      success: nextQuestion
        ? {
            title: 'Saved as a draft',
            copy: 'Only you can see it until you publish. Next question is ready.'
          }
        : {
            title: 'Saved as a draft',
            copy: 'Only you can see it until you publish it from your profile.'
          },
      error: '',
    }))
    this.queueNoticeClear()

    const upload = new DirectUpload(
      file,
      directUploadUrl,
      this
    )

    upload.create(async (error, uploadedBlob) => {
      if (error) {
        console.error('Direct upload failed', error)
        this.setState((prev) => ({
          error: `“${question}” could not be uploaded. Try it again.`,
          success: null,
          uploadingQuestion: prev.uploadingQuestion === question ? null : prev.uploadingQuestion,
          uploadsInFlight: Math.max(0, prev.uploadsInFlight - 1),
          uploadProgress: 0,
          answeredQuestions: prev.answeredQuestions.filter((item) => item !== question),
        }))
        return
      }

      const response = await post(submitVideoPath, {
        body: JSON.stringify({
          applicant_video: {
            question,
            video: uploadedBlob.signed_id
          }
        }),
        responseKind: 'js'
      })

      if (response.ok) {
        this.setState((prev) => ({
          uploadingQuestion: prev.uploadingQuestion === question ? null : prev.uploadingQuestion,
          uploadsInFlight: Math.max(0, prev.uploadsInFlight - 1),
          uploadProgress: 0,
        }))

        const js = await response.text
        Function(js)()
      } else {
        this.setState((prev) => ({
          error: `There was an error saving “${question}”. Please try again.`,
          success: null,
          uploadingQuestion: prev.uploadingQuestion === question ? null : prev.uploadingQuestion,
          uploadsInFlight: Math.max(0, prev.uploadsInFlight - 1),
          uploadProgress: 0,
          answeredQuestions: prev.answeredQuestions.filter((item) => item !== question),
        }))
      }
    })
  }

  queueNoticeClear = () => {
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
    this.noticeTimer = setTimeout(() => {
      this.setState({ success: null })
    }, 4500)
  }

  directUploadWillStoreFileWithXHR = (request) => {
    request.upload.addEventListener("progress", event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress = (event) => {
    const progress = (event.loaded / event.total) * 100.0;
    this.setState({
      uploadProgress: progress
    })
  }

  handleRecordingStart = () => {
    this.setState({
      isRecording: true,
      hasTake: false,
      error: '',
      success: null
    })
  }

  handleTakeReady = () => {
    this.setState({
      isRecording: false,
      hasTake: true
    })
  }

  handleRecordingCompleted = () => {
    this.setState({
      isRecording: false,
      hasTake: false
    })
  }

  onVideoSaved = (event) => {
    const question = event.detail && event.detail.question
    if (!question) return
    this.setState((prev) => ({
      answeredQuestions: prev.answeredQuestions.includes(question)
        ? prev.answeredQuestions
        : prev.answeredQuestions.concat(question)
    }))
  }

  onVideoDeleted = (event) => {
    const question = event.detail && event.detail.question
    if (!question) return
    this.setState((prev) => {
      const answeredQuestions = prev.answeredQuestions.filter((item) => item !== question)
      return {
        answeredQuestions,
        question: prev.question || this.nextUnanswered(answeredQuestions, prev.question)
      }
    })
  }

  onRerecord = (event) => {
    const question = event.detail && event.detail.question
    if (!question) return
    this.openForQuestion(question)
  }

  componentDidMount() {
    this.setState({ question: this.pickQuestion() })
    window.addEventListener('videebop:video-saved', this.onVideoSaved)
    window.addEventListener('videebop:video-deleted', this.onVideoDeleted)
    window.addEventListener('videebop:rerecord', this.onRerecord)
  }

  componentWillUnmount() {
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
    window.removeEventListener('videebop:video-saved', this.onVideoSaved)
    window.removeEventListener('videebop:video-deleted', this.onVideoDeleted)
    window.removeEventListener('videebop:rerecord', this.onRerecord)
  }

  render() {
    const {
      question,
      topicId,
      modalOpen,
      bankOpen,
      isRecording,
      error,
      uploadProgress,
      success,
      uploadingQuestion,
      uploadsInFlight,
      answeredQuestions,
      rerecording
    } = this.state
    const remaining = this.unansweredQuestions(answeredQuestions, topicId)
    const remainingAll = this.unansweredQuestions(answeredQuestions, "all")
    const allDone = remainingAll.length === 0
    const currentTopic = topicId === "all" && question ? topicLabel(topicFor(question)) : topicLabel(topicId)
    const remainingLabel = topicId === "all"
      ? `${remainingAll.length} left`
      : `${currentTopic} · ${remaining.length} left`

    return(
      <div className="profile-recorder-trigger">
        <button type="button" onClick={this.openModal} className="btn btn-danger profile-recorder__open">
          Record a video
        </button>
        <ReactModal
          isOpen={modalOpen}
          onRequestClose={this.requestClose}
          appElement={document.querySelector('body')}
          className="profile-recorder-modal"
          overlayClassName="profile-recorder-overlay"
          style={{
            overlay: { position: 'fixed', inset: 0, zIndex: 1100, backgroundColor: 'rgba(12, 14, 28, 0.72)' },
            content: {
              position: 'relative',
              inset: 'auto',
              border: 'none',
              padding: 0,
              overflow: 'hidden',
              borderRadius: 0,
              background: '#fff',
            }
          }}
        >
          <div className={`profile-recorder${bankOpen ? " is-bank-open" : ""}`}>
            <div className="profile-recorder__top">
              <h2 className="profile-recorder__title">Record a video</h2>
              <button
                type="button"
                className="profile-recorder__bank-toggle"
                onClick={() => this.setState({ bankOpen: !bankOpen })}
              >
                {bankOpen ? "Camera" : "Questions"}
              </button>
              <button className="profile-recorder__close btn btn-outline-danger" onClick={this.requestClose} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={24} height={24}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            { bankOpen && (
              <button
                type="button"
                className="profile-recorder__bank-scrim"
                aria-label="Close questions"
                onClick={() => this.setState({ bankOpen: false })}
              />
            )}
            <aside className="profile-recorder__bank">
              <QuestionBank
                topicId={topicId}
                question={question}
                answeredQuestions={answeredQuestions}
                onTopic={this.handleTopic}
                onSelect={this.handleSelectQuestion}
              />
            </aside>

            <div className="profile-recorder__main">
              { allDone && !question ? (
                <div className="profile-recorder__done">
                  <p className="profile-recorder__prompt">{success ? success.title : 'You’ve answered every question.'}</p>
                  <p className="profile-recorder__done-copy">
                    {success ? success.copy : 'Pick one from the question bank to re-record it as a draft.'}
                  </p>
                </div>
              ) : (
                <React.Fragment>
                  <div className="profile-recorder__question">
                    <div className="profile-recorder__meta">
                      <span className="profile-recorder__remaining">{remainingLabel}</span>
                      { this.nextUnanswered() && (
                        <button
                          type="button"
                          className="profile-recorder__shuffle"
                          onClick={this.handleNext}
                        >
                          Next
                        </button>
                      )}
                      { remaining.length > 1 && !rerecording && (
                        <button
                          type="button"
                          className="profile-recorder__shuffle"
                          onClick={this.handleShuffle}
                        >
                          Shuffle
                        </button>
                      )}
                    </div>
                    <p className="profile-recorder__prompt">{question}</p>
                    { rerecording && (
                      <p className="profile-recorder__replace-note">Keeping this take replaces your current video and saves it as a draft.</p>
                    )}
                  </div>

                  { modalOpen && question && (
                    <VideoRecorder
                      question={question}
                      pendingQuestion={uploadingQuestion}
                      notice={success}
                      onAcceptedVideo={this.handleAcceptedVideo}
                      onRecordingStart={this.handleRecordingStart}
                      onTakeReady={this.handleTakeReady}
                      onRecordingResult={this.handleRecordingCompleted}
                    />
                  )}
                </React.Fragment>
              )}
            </div>

            <div className="profile-recorder__footer">
              <p className="profile-recorder__tips">
                Pick a topic, record, and the next unanswered prompt is queued. Clips stay drafts until you publish.
              </p>

              { uploadsInFlight > 0 && (
                <div className="alert alert-primary p-2 my-2">
                  <p className="mb-1">
                    {uploadsInFlight > 1 ? `Saving ${uploadsInFlight} clips…` : `Saving “${uploadingQuestion}”`}
                  </p>
                  { uploadProgress > 0 && uploadsInFlight === 1 && (
                    <div id="direct-upload" className="direct-upload" style={{ background: '#fff' }}>
                      <div id="direct-upload-progress" className="direct-upload__progress" style={{ width: `${uploadProgress}%`}}></div>
                      <span className="direct-upload__filename" id="progress-text">{Math.round(uploadProgress)}%</span>
                    </div>
                  )}
                </div>
              )}

              { error && (
                <div className="alert alert-danger text-center my-2" role="alert">
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </ReactModal>
      </div>
    )
  }
}

ProfileVideoRecorder.propTypes = {
  directUploadUrl: PropTypes.string.isRequired,
  submitVideoPath: PropTypes.string.isRequired,
  answeredQuestions: PropTypes.arrayOf(PropTypes.string)
}

ProfileVideoRecorder.defaultProps = {
  answeredQuestions: []
}

export default ProfileVideoRecorder;
